import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { XlsxImportExport } from '../../components/XlsxImportExport';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Package, ExternalLink, ImageIcon, TrendingDown, XCircle, CheckCircle2, Upload, Trash2, Download, FileSpreadsheet, Settings as SettingsIcon, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthImage — loads a protected image via fetch (with Authorization header)
 * and renders it from a blob URL. Used so we don't have to embed base64
 * images inside the parts-catalog list response (see backend has_image flag).
 */
function AuthImage({ partId, alt, className, headers, onError }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    fetch(`${API_URL}/api/parts-catalog/${partId}/image`, { headers })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(b);
        setUrl(objUrl);
      })
      .catch(() => onError && onError());
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [partId, headers, onError]);
  if (!url) return <div className={`${className} bg-slate-900 animate-pulse`} aria-label={alt} data-testid={`part-image-loading-${partId}`} />;
  return <img src={url} alt={alt} className={className} data-testid={`part-image-${partId}`} />;
}

const R = (n) => n == null ? '-' : 'R ' + Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return ''; }
};

// JSM sales-reference price — discount + threshold come from backend settings
const DEFAULT_JSM_DISCOUNT = 35;
const DEFAULT_JSM_THRESHOLD = 400;
const computeJsmPrice = (catalogPrice, discountPct, threshold) => {
  const p = Number(catalogPrice) || 0;
  if (p <= (threshold ?? 0)) return null;
  return Math.round(p * (1 - (discountPct ?? 0) / 100) * 100) / 100;
};

/**
 * PartsCatalogPanel — embeddable panel (no DashboardLayout wrapper).
 * Used inside the Auto Spares admin + salesman dashboards as a tab.
 *
 * Props:
 *   - readOnly: if true, hides all mutation controls (add image / remove image)
 */
export default function PartsCatalogPanel({ readOnly = false }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pre-fill search from URL ?search=XYZ (used when salesman clicks "Open Catalog" link)
  const _initialSearch = (() => {
    try { return new URLSearchParams(window.location.search).get('search') || ''; }
    catch { return ''; }
  })();
  const [search, setSearch] = useState(_initialSearch);
  const [category, setCategory] = useState('all');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [totals, setTotals] = useState({ total: 0, in_stock: 0 });
  const [imageCoverage, setImageCoverage] = useState(null); // {total, with_image, without_image, percent, grade, label}
  const [uploadingId, setUploadingId] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importingPricelist, setImportingPricelist] = useState(false);
  const [jsmSettings, setJsmSettings] = useState({ discount_percent: DEFAULT_JSM_DISCOUNT, min_price_threshold: DEFAULT_JSM_THRESHOLD });
  const [jsmDialogOpen, setJsmDialogOpen] = useState(false);
  const [jsmForm, setJsmForm] = useState({ discount_percent: DEFAULT_JSM_DISCOUNT, min_price_threshold: DEFAULT_JSM_THRESHOLD });
  const [jsmSaving, setJsmSaving] = useState(false);
  // Part CRUD dialog state
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [partEditing, setPartEditing] = useState(null);
  const [partForm, setPartForm] = useState({ sku: '', name: '', category: '', year_range: '', sale_price: '', retail_price: '', in_stock: true });
  const [partSaving, setPartSaving] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const pendingPartIdRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Decode role from JWT (no separate API call). Token format header.payload.sig
  const isAdmin = (() => {
    try {
      const payload = JSON.parse(atob((token || '').split('.')[1] || ''));
      const role = payload?.role || '';
      return ['admin', 'auto_spares_admin', 'auto_spares_super'].includes(role);
    } catch { return false; }
  })();

  const handleDeleteAllParts = async () => {
    if (!window.confirm('This will DELETE the ENTIRE Parts Catalog (every part, every price).\n\nThis cannot be undone. Are you sure?')) return;
    const typed = window.prompt('Type DELETE in capital letters to confirm:');
    if ((typed || '').trim() !== 'DELETE') {
      toast.error('Cancelled — confirmation text did not match');
      return;
    }
    setDeletingAll(true);
    try {
      const res = await axios.delete(`${API_URL}/api/parts-catalog/all`, {
        headers,
        params: { confirm: 'DELETE' },
      });
      toast.success(`Deleted ${res.data.deleted} parts`);
      await Promise.all([fetchParts(), fetchCategories()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete-all failed');
    } finally {
      setDeletingAll(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts-catalog/categories`, { headers });
      setCategories(res.data.categories || []);
      setTotals({ total: res.data.total || 0, in_stock: res.data.in_stock || 0 });
    } catch (err) {
      console.error('fetchCategories failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchImageCoverage = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts-catalog/image-coverage`, { headers });
      setImageCoverage(res.data);
    } catch (err) {
      console.error('fetchImageCoverage failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJsmSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts-catalog/jsm-settings`, { headers });
      const s = {
        discount_percent: Number(res.data?.discount_percent ?? DEFAULT_JSM_DISCOUNT),
        min_price_threshold: Number(res.data?.min_price_threshold ?? DEFAULT_JSM_THRESHOLD),
      };
      setJsmSettings(s);
      setJsmForm(s);
    } catch (err) {
      console.error('fetchJsmSettings failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsmPrice = useCallback((catalogPrice) =>
    computeJsmPrice(catalogPrice, jsmSettings.discount_percent, jsmSettings.min_price_threshold),
  [jsmSettings]);

  const saveJsmSettings = async () => {
    const discount = Number(jsmForm.discount_percent);
    const threshold = Number(jsmForm.min_price_threshold);
    if (!(discount >= 0 && discount < 100)) {
      toast.error('Discount must be between 0 and 99');
      return;
    }
    if (!(threshold >= 0)) {
      toast.error('Threshold must be 0 or greater');
      return;
    }
    setJsmSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/parts-catalog/jsm-settings`,
        { discount_percent: discount, min_price_threshold: threshold },
        { headers }
      );
      setJsmSettings({
        discount_percent: Number(res.data.discount_percent),
        min_price_threshold: Number(res.data.min_price_threshold),
      });
      toast.success('JSM price settings saved');
      setJsmDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setJsmSaving(false);
    }
  };

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category && category !== 'all') params.category = category;
      if (inStockOnly) params.in_stock_only = true;
      const res = await axios.get(`${API_URL}/api/parts-catalog`, { headers, params });
      setParts(res.data);
    } catch {
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, inStockOnly]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchImageCoverage(); }, [fetchImageCoverage]);
  useEffect(() => { fetchJsmSettings(); }, [fetchJsmSettings]);
  useEffect(() => {
    const t = setTimeout(fetchParts, 250);
    return () => clearTimeout(t);
  }, [fetchParts]);

  // Fetch historical sales reference whenever search changes
  // (Sales History feature removed per user request — Apr 2026)

  // Lightweight search-dropdown suggestions
  const quickMatches = useMemo(() => {
    if (!search.trim() || search.length < 2) return [];
    return parts.slice(0, 8);
  }, [search, parts]);

  // ---------- IMAGE UPLOAD ----------
  const triggerUpload = (partId) => {
    pendingPartIdRef.current = partId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    const partId = pendingPartIdRef.current;
    if (!file || !partId) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      toast.error('Only PNG / JPG / WEBP / GIF allowed');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be smaller than 3MB');
      return;
    }
    setUploadingId(partId);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(
        `${API_URL}/api/parts-catalog/${partId}/upload-image`,
        { image_base64: base64 },
        { headers }
      );
      toast.success('Image uploaded');
      fetchParts();
      fetchImageCoverage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
      pendingPartIdRef.current = null;
    }
  };

  const handleRemoveImage = async (partId) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      await axios.delete(`${API_URL}/api/parts-catalog/${partId}/image`, { headers });
      toast.success('Image removed');
      fetchParts();
      fetchImageCoverage();
    } catch {
      toast.error('Remove failed');
    }
  };

  // ---------- PART CRUD ----------
  const openAddPartDialog = () => {
    setPartEditing(null);
    setPartForm({ sku: '', name: '', category: '', year_range: '', sale_price: '', retail_price: '', in_stock: true });
    setPartDialogOpen(true);
  };

  const openEditPartDialog = (p) => {
    setPartEditing(p);
    setPartForm({
      sku: p.sku || '',
      name: p.name || '',
      category: p.category || '',
      year_range: p.year_range || '',
      sale_price: p.sale_price ?? '',
      retail_price: p.retail_price ?? '',
      in_stock: !!p.in_stock,
    });
    setPartDialogOpen(true);
  };

  const handleSavePart = async () => {
    const sku = (partForm.sku || '').trim().toUpperCase();
    const name = (partForm.name || '').trim();
    if (!sku || !name) {
      toast.error('SKU and Name are required');
      return;
    }
    const sale = Number(partForm.sale_price);
    if (!(sale >= 0)) {
      toast.error('Sale price must be a number ≥ 0');
      return;
    }
    const payload = {
      sku,
      name,
      category: partForm.category || 'Other',
      year_range: partForm.year_range || '',
      sale_price: sale,
      retail_price: partForm.retail_price !== '' ? Number(partForm.retail_price) : null,
      in_stock: !!partForm.in_stock,
    };
    setPartSaving(true);
    try {
      if (partEditing) {
        await axios.put(`${API_URL}/api/parts-catalog/${partEditing.id}`, payload, { headers });
        toast.success(`Updated ${sku}`);
      } else {
        await axios.post(`${API_URL}/api/parts-catalog`, payload, { headers });
        toast.success(`Created ${sku}`);
      }
      setPartDialogOpen(false);
      fetchParts();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setPartSaving(false);
    }
  };

  const handleDeletePart = async (p) => {
    if (!window.confirm(`Delete ${p.sku} — "${p.name}"?\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/parts-catalog/${p.id}`, { headers });
      toast.success(`Deleted ${p.sku}`);
      fetchParts();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // ---------- EXPORT CSV ----------
  const handleExportCsv = async (includeImages = false) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/parts-catalog/export-csv`, {
        headers,
        params: { include_images: includeImages },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `parts_catalog_${today}${includeImages ? '_with_images' : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${parts.length ? 'catalog' : 'empty catalog'} to CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ---------- BULK UPLOAD CSV ----------
  const triggerBulkUpload = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
      csvInputRef.current.click();
    }
  };

  const handleImportJsmPricelist = async () => {
    if (!window.confirm('Replace the catalog with the JSM Complete Stock List?\n\nThis WIPES the current catalog and imports the master 91-item stock list (Brakes, Body, Electrical, Engine, Transmission, K7M family, Door sub-components). Use this once when seeding a new database or to reset back to the master list.')) return;
    setImportingPricelist(true);
    try {
      const res = await axios.post(`${API_URL}/api/parts-catalog/import-jsm-pricelist?wipe=true`, {}, { headers });
      const { wiped, created, updated, total_in_catalog } = res.data;
      toast.success(`Stock list loaded — ${wiped} removed, ${created} new, ${updated} updated. Catalog now has ${total_in_catalog} parts.`);
      await Promise.all([fetchCategories(), fetchParts(), fetchImageCoverage()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load JSM stock list');
    } finally {
      setImportingPricelist(false);
    }
  };

  const handleBulkCsvChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error('Please select a .csv file');
      return;
    }
    if (!window.confirm(
      `Bulk upload "${file.name}"?\n\n` +
      '• Existing SKUs will be UPDATED with the CSV values\n' +
      '• New SKUs will be CREATED\n' +
      '• Rows missing SKU or name will be skipped\n\n' +
      'A change-log CSV will be auto-downloaded afterwards.'
    )) return;

    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/parts-catalog/bulk-upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const { created = 0, updated = 0, skipped = 0, change_log = [] } = res.data || {};
      toast.success(`Upload complete: ${created} created, ${updated} updated, ${skipped} skipped`);

      // Build and download change-log CSV
      if (change_log.length > 0) {
        const header = ['row', 'sku', 'name', 'action', 'sale_price', 'reason'];
        const rows = change_log.map(r => header.map(h => {
          const v = r[h];
          if (v === undefined || v === null) return '';
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        }).join(','));
        const csv = '\uFEFF' + header.join(',') + '\n' + rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `parts_upload_changelog_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      await fetchCategories();
      await fetchParts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  // ---------- LINK SALE TO SKU ---------- (removed with Sales History panel)

  return (
    <div className="space-y-5">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
        data-testid="parts-image-file-input"
      />
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleBulkCsvChange}
        data-testid="parts-bulk-csv-input"
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Package className="h-5 w-5 text-emerald-400" /> Parts Price Catalog
          </h2>
          <p className="text-slate-400 text-sm">
            NP200 &amp; NP300 parts — search by SKU, name or category. Sale &amp; retail pricing.
            {!readOnly && <span className="ml-1 text-emerald-300">Upload an image to each part from your PC.</span>}
          </p>
        </div>
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExportCsv(false)}
              disabled={exporting}
              className="bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
              data-testid="export-catalog-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExportCsv(true)}
              disabled={exporting}
              className="bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
              title="Includes image data-URLs — produces a large file"
              data-testid="export-catalog-csv-with-images-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export + Images
            </Button>
            <Button
              size="sm"
              onClick={triggerBulkUpload}
              disabled={bulkUploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-upload-csv-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              {bulkUploading ? 'Uploading...' : 'Bulk Upload CSV'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setJsmForm(jsmSettings); setJsmDialogOpen(true); }}
              className="bg-slate-900 border-amber-700/60 text-amber-300 hover:bg-amber-950/40"
              title={`JSM Price: ${jsmSettings.discount_percent}% off catalog for parts above R${jsmSettings.min_price_threshold}`}
              data-testid="jsm-settings-btn"
            >
              <SettingsIcon className="h-4 w-4 mr-1" />
              JSM Price ({jsmSettings.discount_percent}%)
            </Button>
            <Button
              size="sm"
              onClick={openAddPartDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-part-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Part
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleImportJsmPricelist}
              disabled={importingPricelist}
              className="bg-slate-900 border-cyan-700/60 text-cyan-300 hover:bg-cyan-950/40"
              title="Load the 66-item JSM master price list (Engine, Body, Brakes, Lighting, etc.) into this catalog. Safe to re-run — only updates prices."
              data-testid="import-jsm-pricelist-btn"
            >
              <Upload className="h-4 w-4 mr-1" />
              {importingPricelist ? 'Loading…' : 'Load Complete Stock List'}
            </Button>
            <XlsxImportExport
              exportPath="/api/parts-catalog/export-xlsx"
              importPath="/api/parts-catalog/import-xlsx"
              label="Parts Catalog"
              testIdPrefix="parts-catalog"
              onImported={fetchParts}
            />
            {isAdmin && (
              <Button
                size="sm"
                onClick={handleDeleteAllParts}
                disabled={deletingAll}
                className="bg-rose-700 hover:bg-rose-800 text-white"
                title="Wipe the entire Parts Catalog — admin only"
                data-testid="delete-all-parts-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deletingAll ? 'Deleting…' : 'Delete All'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4">
          <div className="text-xs text-slate-400 uppercase mb-1">Total Parts</div>
          <div className="text-2xl font-bold text-white">{totals.total}</div>
        </CardContent></Card>
        <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4">
          <div className="text-xs text-slate-400 uppercase mb-1">In Stock</div>
          <div className="text-2xl font-bold text-emerald-400">{totals.in_stock}</div>
        </CardContent></Card>
        <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4">
          <div className="text-xs text-slate-400 uppercase mb-1">Categories</div>
          <div className="text-2xl font-bold text-white">{categories.length}</div>
        </CardContent></Card>
        <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4">
          <div className="text-xs text-slate-400 uppercase mb-1">Filtered Results</div>
          <div className="text-2xl font-bold text-indigo-400">{parts.length}</div>
        </CardContent></Card>
        <Card
          className={`border-slate-700 text-white ${
            imageCoverage?.grade === 'A' ? 'bg-emerald-600/30 border-emerald-500/50' :
            imageCoverage?.grade === 'B' ? 'bg-cyan-600/30 border-cyan-500/50' :
            imageCoverage?.grade === 'C' ? 'bg-sky-600/30 border-sky-500/50' :
            imageCoverage?.grade === 'D' ? 'bg-amber-600/30 border-amber-500/50' :
            'bg-rose-600/30 border-rose-500/50'
          }`}
          data-testid="image-coverage-kpi"
        >
          <CardContent className="p-4">
            <div className="text-xs text-slate-300 uppercase mb-1 flex items-center gap-1">
              Image Coverage
              {imageCoverage?.grade && (
                <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/30" data-testid="image-coverage-grade">
                  {imageCoverage.grade} · {imageCoverage.label}
                </span>
              )}
            </div>
            <div className="text-2xl font-bold" data-testid="image-coverage-percent">
              {imageCoverage ? `${imageCoverage.percent}%` : '—'}
            </div>
            {imageCoverage && (
              <>
                <div className="text-[10px] text-slate-200/80 mt-0.5" data-testid="image-coverage-counts">
                  {imageCoverage.with_image}/{imageCoverage.total} parts · {imageCoverage.without_image} missing
                </div>
                <div className="h-1.5 w-full bg-slate-900/60 rounded overflow-hidden mt-2">
                  <div
                    className={`h-full transition-all ${
                      imageCoverage.grade === 'A' ? 'bg-emerald-400' :
                      imageCoverage.grade === 'B' ? 'bg-cyan-400' :
                      imageCoverage.grade === 'C' ? 'bg-sky-400' :
                      imageCoverage.grade === 'D' ? 'bg-amber-400' :
                      'bg-rose-400'
                    }`}
                    style={{ width: `${imageCoverage.percent}%` }}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search + Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white">Search &amp; Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[280px]">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by SKU (e.g. AC-21382), name (e.g. door mirror), or category..."
                className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="parts-search-input"
              />
              {quickMatches.length > 0 && search.length >= 2 && (
                <div className="absolute top-full left-0 right-0 bg-slate-800 border border-slate-700 rounded-lg shadow-xl mt-1 z-20 max-h-72 overflow-y-auto">
                  {quickMatches.map(p => (
                    <div
                      key={p.id}
                      className="px-3 py-2 hover:bg-slate-700 flex items-center gap-2 border-b border-slate-700 last:border-0"
                      data-testid={`search-suggestion-${p.sku}`}
                    >
                      <button
                        onClick={() => setSearch(p.sku)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      >
                        {p.has_image ? (
                          <AuthImage
                            partId={p.id}
                            alt={p.sku}
                            className="w-10 h-10 object-cover rounded bg-slate-900"
                            headers={headers}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-slate-900 flex items-center justify-center"><ImageIcon className="h-4 w-4 text-slate-500" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs text-slate-400">{p.sku}</div>
                          <div className="text-sm truncate text-white">{p.name}</div>
                        </div>
                        <div className="text-right pr-1">
                          <div className="text-emerald-400 font-bold text-sm">{R(p.sale_price)}</div>
                          {p.retail_price ? <div className="text-xs text-slate-500 line-through">{R(p.retail_price)}</div> : null}
                          {jsmPrice(p.sale_price) != null && (
                            <div className="text-[10px] text-amber-400 font-semibold">JSM {R(jsmPrice(p.sale_price))}</div>
                          )}
                        </div>
                      </button>
                      {!readOnly && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={(e) => { e.stopPropagation(); openEditPartDialog(p); }}
                            title="Edit this part"
                            data-testid={`search-edit-${p.sku}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 w-7 p-0 bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={(e) => { e.stopPropagation(); handleDeletePart(p); }}
                            title="Delete this part"
                            data-testid={`search-delete-${p.sku}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!readOnly && (
              <Button
                onClick={openAddPartDialog}
                className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                data-testid="add-part-from-search-btn"
                title="Add a brand-new part to the catalog"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Part
              </Button>
            )}
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-52 bg-slate-900 border-slate-700 text-white" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant={inStockOnly ? 'default' : 'outline'}
              onClick={() => setInStockOnly(!inStockOnly)}
              className={inStockOnly ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 border-slate-700 text-white hover:bg-slate-700'}
              data-testid="in-stock-toggle"
            >
              {inStockOnly ? <CheckCircle2 className="h-4 w-4 mr-1" /> : null}
              In Stock Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Parts grid */}
      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading parts...</div>
      ) : parts.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-10 text-center text-slate-400">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>No parts match your filters.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {parts.map(p => (
            <Card key={p.id} className="bg-slate-800/50 border-slate-700 hover:border-emerald-600 transition-colors overflow-hidden" data-testid={`part-card-${p.sku}`}>
              <div className="relative bg-slate-900 h-44 flex items-center justify-center overflow-hidden">
                {p.has_image ? (
                  <AuthImage
                    partId={p.id}
                    alt={p.name}
                    className="max-w-full max-h-full object-contain"
                    headers={headers}
                  />
                ) : (
                  <div className="text-slate-600 flex flex-col items-center">
                    <ImageIcon className="h-12 w-12 mb-1" />
                    <span className="text-xs">No image yet</span>
                  </div>
                )}

                {!p.in_stock && (
                  <Badge variant="destructive" className="absolute top-2 left-2">
                    <XCircle className="h-3 w-3 mr-1" /> Out of Stock
                  </Badge>
                )}
                <Badge variant="outline" className="absolute top-2 right-2 bg-slate-900/80 border-slate-700 text-slate-200 text-[10px]">
                  {p.category}
                </Badge>

                {!readOnly && (
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 px-2 bg-slate-900/90 hover:bg-slate-700 text-white text-xs"
                      onClick={() => openEditPartDialog(p)}
                      title="Edit part"
                      data-testid={`edit-part-btn-${p.sku}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 px-2 bg-slate-900/90 hover:bg-slate-700 text-white text-xs"
                      disabled={uploadingId === p.id}
                      onClick={() => triggerUpload(p.id)}
                      data-testid={`upload-image-btn-${p.sku}`}
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      {uploadingId === p.id ? 'Uploading...' : (p.has_image ? 'Replace' : 'Upload')}
                    </Button>
                    {p.has_image && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 w-7 p-0"
                        onClick={() => handleRemoveImage(p.id)}
                        data-testid={`remove-image-btn-${p.sku}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 w-7 p-0"
                      onClick={() => handleDeletePart(p)}
                      title="Delete part from catalog"
                      data-testid={`delete-part-btn-${p.sku}`}
                    >
                      <XCircle className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-slate-400">{p.sku}</span>
                  {p.retail_price && p.retail_price > p.sale_price && (
                    <Badge className="bg-amber-500 text-[10px]"><TrendingDown className="h-3 w-3 mr-0.5" /> SALE</Badge>
                  )}
                </div>
                <p className="text-sm font-medium leading-tight line-clamp-2 min-h-[2.5rem] text-white">{p.name}</p>
                <p className="text-xs text-slate-400">{p.year_range}</p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-lg font-bold text-emerald-400">{R(p.sale_price)}</span>
                  {p.retail_price && p.retail_price > p.sale_price && (
                    <span className="text-xs text-slate-500 line-through">{R(p.retail_price)}</span>
                  )}
                </div>
                {p.min_price != null && p.max_price != null && p.min_price !== p.max_price && (
                  <div
                    className="text-[10px] text-slate-400"
                    data-testid={`price-range-${p.sku}`}
                  >
                    Range: <span className="text-slate-200 font-semibold">{R(p.min_price)}</span> – <span className="text-slate-200 font-semibold">{R(p.max_price)}</span>
                  </div>
                )}
                {jsmPrice(p.sale_price) != null && (
                  <div
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-600/60 bg-amber-950/40 px-2 py-1"
                    data-testid={`jsm-price-${p.sku}`}
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-400">JSM Price</span>
                    <span className="text-sm font-bold text-amber-300">{R(jsmPrice(p.sale_price))}</span>
                    <span className="text-[9px] text-amber-500/70">(-{jsmSettings.discount_percent}%)</span>
                  </div>
                )}
                <a
                  href={p.aceauto_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline"
                >
                  View on Ace Auto <ExternalLink className="h-3 w-3" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* JSM Price settings dialog */}
      <Dialog open={jsmDialogOpen} onOpenChange={setJsmDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="jsm-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <SettingsIcon className="h-5 w-5 text-amber-400" />
              JSM Price Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-400">
              The JSM Price chip is shown on every catalog card as a sales reference.
              Changes apply to everyone viewing the catalog.
            </p>

            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wide">Discount %</label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="99"
                value={jsmForm.discount_percent}
                onChange={(e) => setJsmForm({ ...jsmForm, discount_percent: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
                data-testid="jsm-discount-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">E.g. 35 means 35% below the catalog `sale_price`.</p>
            </div>

            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wide">Minimum Price Threshold (R)</label>
              <Input
                type="number"
                step="1"
                min="0"
                value={jsmForm.min_price_threshold}
                onChange={(e) => setJsmForm({ ...jsmForm, min_price_threshold: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700 text-white"
                data-testid="jsm-threshold-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Only parts priced above this amount show a JSM price chip.</p>
            </div>

            {/* Live preview */}
            <div className="rounded-md border border-amber-700/50 bg-amber-950/30 p-3">
              <div className="text-[10px] text-amber-400 uppercase tracking-wide mb-1">Preview</div>
              <div className="text-sm text-slate-300">
                Catalog price <span className="font-semibold text-emerald-400">R 1 000,00</span> →{' '}
                <span className="font-bold text-amber-300">
                  JSM {R(computeJsmPrice(1000, Number(jsmForm.discount_percent) || 0, Number(jsmForm.min_price_threshold) || 0))}
                </span>
              </div>
              <div className="text-sm text-slate-300">
                Catalog price <span className="font-semibold text-emerald-400">R 350,00</span> →{' '}
                <span className="text-slate-500">
                  {computeJsmPrice(350, Number(jsmForm.discount_percent) || 0, Number(jsmForm.min_price_threshold) || 0) == null
                    ? 'hidden (below threshold)'
                    : R(computeJsmPrice(350, Number(jsmForm.discount_percent) || 0, Number(jsmForm.min_price_threshold) || 0))}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setJsmDialogOpen(false)}
              className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              data-testid="jsm-settings-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveJsmSettings}
              disabled={jsmSaving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="jsm-settings-save-btn"
            >
              {jsmSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Part dialog */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle>{partEditing ? `Edit Part — ${partEditing.sku}` : 'Add New Part'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">SKU *</label>
                <Input
                  value={partForm.sku}
                  onChange={(e) => setPartForm({ ...partForm, sku: e.target.value })}
                  placeholder="e.g. AC-12345"
                  disabled={!!partEditing}
                  className="bg-slate-800 border-slate-700"
                  data-testid="part-sku-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Category</label>
                <Input
                  value={partForm.category}
                  onChange={(e) => setPartForm({ ...partForm, category: e.target.value })}
                  placeholder="e.g. Mirrors"
                  list="part-categories"
                  className="bg-slate-800 border-slate-700"
                  data-testid="part-category-input"
                />
                <datalist id="part-categories">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Part Name *</label>
              <Input
                value={partForm.name}
                onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                placeholder="e.g. NP200 Right Door Mirror"
                className="bg-slate-800 border-slate-700"
                data-testid="part-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Year / Fitment</label>
              <Input
                value={partForm.year_range}
                onChange={(e) => setPartForm({ ...partForm, year_range: e.target.value })}
                placeholder="e.g. 2008 to Current"
                className="bg-slate-800 border-slate-700"
                data-testid="part-year-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Sale Price (R) *</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={partForm.sale_price}
                  onChange={(e) => setPartForm({ ...partForm, sale_price: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="part-sale-price-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Retail Price (R)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={partForm.retail_price}
                  onChange={(e) => setPartForm({ ...partForm, retail_price: e.target.value })}
                  placeholder="(optional)"
                  className="bg-slate-800 border-slate-700"
                  data-testid="part-retail-price-input"
                />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={partForm.in_stock}
                onChange={(e) => setPartForm({ ...partForm, in_stock: e.target.checked })}
                className="h-4 w-4"
                data-testid="part-in-stock-checkbox"
              />
              <CheckCircle2 className="h-4 w-4 text-emerald-400" /> In Stock
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartDialogOpen(false)}
              className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700">
              Cancel
            </Button>
            <Button onClick={handleSavePart} disabled={partSaving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-part-btn">
              {partSaving ? 'Saving…' : (partEditing ? 'Save Changes' : 'Create Part')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
